/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {ListMenuContent} from './list-menu-content'
import {HeaderDown, HeaderUp} from '../icons'
import {Box, Text, useMultiStyleConfig, Slide, IconButton} from '@chakra-ui/react'
import {getGlobalLink} from '../../utils/contentstack-utils'
import {useIntl} from 'react-intl'

const ListMenuPopover = ({
    primarycategory,
    categoriesData,
    selectedMenuItem,
    setSelectedMenuItem,
    onItemClick,
}) => {
    const intl = useIntl()
    const styles = useMultiStyleConfig('ListMenu')

    const isOpen = selectedMenuItem === primarycategory.uid
    const [delayedIsOpen, setDelayedIsOpen] = useState(selectedMenuItem === primarycategory.uid)
    const menuCloseTimeoutRef = useRef(null)

    const keyMap = {
        Escape: () => setSelectedMenuItem(null),
        Enter: () =>
            isOpen ? setSelectedMenuItem(null) : setSelectedMenuItem(primarycategory.uid),
    }

    const {url, label, isAbsolute} = getGlobalLink({
        link: primarycategory?.redirects,
        categoriesData,
    })

    const categoryID = primarycategory?.redirects?.categorysfcc.data?.[0]?.id ?? primarycategory.uid

    const prevSelectedMenuItemRef = useRef(selectedMenuItem)

    useEffect(() => {
        if (isOpen) {
            if (menuCloseTimeoutRef.current) {
                clearTimeout(menuCloseTimeoutRef.current)
                menuCloseTimeoutRef.current = null
            }
            setDelayedIsOpen(true)
        } else {
            const isCompleteClose = selectedMenuItem === null

            const wasTabChange =
                prevSelectedMenuItemRef.current !== null && selectedMenuItem !== null

            // Only apply delay when completely closing the menu, not when switching tabs
            if (isCompleteClose && !wasTabChange) {
                menuCloseTimeoutRef.current = setTimeout(() => {
                    setDelayedIsOpen(false)
                }, 500)
            } else {
                setDelayedIsOpen(false)
            }
        }

        prevSelectedMenuItemRef.current = selectedMenuItem

        return () => {
            if (menuCloseTimeoutRef.current) {
                clearTimeout(menuCloseTimeoutRef.current)
            }
        }
    }, [isOpen, selectedMenuItem])

    return (
        <Box
            sx={{
                '.hidden-menu': {
                    visibility: 'hidden',
                },
                '.visible-menu': {
                    visibility: 'visible',
                },
            }}
        >
            <Box {...styles.listMenuTriggerContainer}>
                <Box
                    {...styles.listMenuTriggerGroup}
                    onClick={() => setSelectedMenuItem(primarycategory.uid)}
                >
                    <Text
                        {...styles.listMenuTriggerLink}
                        {...(delayedIsOpen ? styles.listMenuTriggerLinkActive : {})}
                        {...{name: label + ' __'}}
                    >
                        {label}
                    </Text>
                    {/* this is a bold duplicate of menu element text to reserve the
                    correct space and stop layout shift when normal -> bold on hover*/}
                    <Text aria-hidden="true" {...styles.listMenuTriggerLinkDuplicate}>
                        {label}
                    </Text>
                    {delayedIsOpen ? (
                        <IconButton
                            icon={<HeaderUp tabIndex="0" boxSize="iconsSize.small" />}
                            onClick={(e) => {
                                keyMap[e.key]?.(e)
                            }}
                            aria-label={intl.formatMessage({
                                id: 'global.expanded-arrow.aria_label',
                                defaultMessage: 'Expanded icon',
                            })}
                        />
                    ) : (
                        <IconButton
                            icon={<HeaderDown tabIndex="0" boxSize="iconsSize.small" />}
                            onClick={(e) => {
                                keyMap[e.key]?.(e)
                            }}
                            aria-label={intl.formatMessage({
                                id: 'global.collapsed-arrow.aria_label',
                                defaultMessage: 'Collapsed icon',
                            })}
                        />
                    )}
                </Box>
            </Box>
            <Box
                data-testid={categoryID}
                className={delayedIsOpen ? 'visible-menu' : 'hidden-menu'}
            >
                <Box
                    {...styles.popoverOverlay}
                    onMouseEnter={() => {
                        // When mouse exits the menu area, start the delayed closing
                        if (isOpen && !menuCloseTimeoutRef.current) {
                            setSelectedMenuItem(null)
                        }
                    }}
                />
                <Slide
                    direction="top"
                    in={delayedIsOpen}
                    style={{position: 'absolute', top: '100%', zIndex: -1}}
                >
                    <Box
                        {...styles.listMenuContent}
                        onMouseEnter={() => {
                            // Cancel closing if user hovers back during delay
                            if (menuCloseTimeoutRef.current) {
                                clearTimeout(menuCloseTimeoutRef.current)
                                menuCloseTimeoutRef.current = null
                                if (!isOpen) {
                                    setSelectedMenuItem(primarycategory.uid)
                                }
                            }
                        }}
                    >
                        <ListMenuContent
                            secondLevel={primarycategory?.second_level}
                            categoriesData={categoriesData}
                            categoryUrl={url}
                            isCategoryUrlAbsoulte={isAbsolute}
                            onItemClick={onItemClick}
                        />
                    </Box>
                </Slide>
            </Box>
        </Box>
    )
}

ListMenuPopover.propTypes = {
    categoriesData: PropTypes.object,
    primarycategory: PropTypes.object,
    selectedMenuItem: PropTypes.string,
    setSelectedMenuItem: PropTypes.func,
    onItemClick: PropTypes.func,
}
export {ListMenuPopover}
